import {FC, lazy, Suspense, ReactNode} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {UpdateProfile} from '../modules/profile/UpdateProfile'
import {UpdatePassword} from '../modules/profile/UpdatePassword'
import {UpdateCompanyProfile} from '../modules/profile/UpdateCompanyProfile'
import {CheckAccess, IsRootUserOrAdmin} from '../services/Functional'
import {EnterPrisePlan} from '../modules/subscription_plans/enterprise_plan/EnterPrisePlan'

const PrivateRoutes = () => {
  const BuilderPageWrapper = lazy(() => import('../pages/layout-builder/BuilderPageWrapper'))
  const Users = lazy(() => import('../modules/users/UsersPage'))
  const Calendar = lazy(() => import('../modules/calendar/CalendarWrapper'))
  const Projects = lazy(() => import('../modules/projects/ProjectsPage'))
  const Events = lazy(() => import('../modules/events/EventsPage'))
  const Sites = lazy(() => import('../modules/sites/SitesPage'))
  const Notifications = lazy(() => import('../modules/notifications/NotificationsPage'))
  const Assets = lazy(() => import('../modules/assets/AssetsPage'))
  const InspectionListing = lazy(() => import('../modules/inspection_listing/InspectionListing'))
  const Frequencies = lazy(() => import('../modules/frequencies/FrequenciesPage'))
  const InspectionTemplates = lazy(() => import('../modules/inspection_templates/TemplatesPage'))
  const Configurations = lazy(() => import('../modules/configurations/Configurations'))
  const Mappings = lazy(() => import('../modules/mapping/Mappings'))
  const Groups = lazy(() => import('../modules/groups/GroupsPage'))
  const TransactionHistory = lazy(
    () => import('../modules/subscription_history/TransactionHistoryPage')
  )
  const Subscriptions = lazy(() => import('../modules/subscription_history/SubscriptionsPage'))
  const SubscriptionPlans = lazy(
    () => import('../modules/subscription_plans/SubscriptionPlansPage')
  )
  const InspectionCompanies = lazy(
    () => import('../modules/inspection_companies/InspectionCompaniesPage')
  )
  const Tags = lazy(() => import('../modules/tags/TagsPage'))
  const Orders = lazy(() => import('../modules/orders/OrdersPage'))
  const Templates = lazy(() => import('../modules/templates/GlobalTemplatesPage'))
  const Categories = lazy(() => import('../modules/categories/CategoriesPage'))
  const DataAnonymization = lazy(() => import('../modules/data_anonymization/DataAnonymizationPage'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path='auth/*' element={<Navigate to={`/dashboard`} />} />
        <Route path='dashboard' element={<DashboardWrapper />} />
        <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='profile/update' element={<UpdateProfile />} />
        <Route path='company/profile' element={<UpdateCompanyProfile />} />
        <Route path='profile/security-overview' element={<UpdatePassword />} />
        <Route
          path='team-management/*'
          element={
            <SuspensedView>
              <Users />
            </SuspensedView>
          }
        />
        <Route
          path='templates/*'
          element={
            <SuspensedView>
              <Templates />
            </SuspensedView>
          }
        />
        <Route
          path='plans/plans-management/*'
          element={
            <SuspensedView>
              <SubscriptionPlans />
            </SuspensedView>
          }
        />
        <Route
          path='plans/enterprise-plan/*'
          element={
            <SuspensedView>
              <EnterPrisePlan />
            </SuspensedView>
          }
        />
        <Route
          path='inspection-companies/*'
          element={
            <SuspensedView>
              <InspectionCompanies />
            </SuspensedView>
          }
        />
        <Route
          path='schedule/*'
          element={
            <SuspensedView>
              <Calendar />
            </SuspensedView>
          }
        />
        <Route
          path='projects/*'
          element={
            <SuspensedView>
              <Projects />
            </SuspensedView>
          }
        />
        <Route
          path='notification-templates/*'
          element={
            <SuspensedView>
              <Events />
            </SuspensedView>
          }
        />
        <Route
          path='sites/*'
          element={
            <SuspensedView>
              <Sites />
            </SuspensedView>
          }
        />
        <Route
          path='notifications/*'
          element={
            <SuspensedView>
              <Notifications />
            </SuspensedView>
          }
        />
        <Route
          path='assets/:asset_id/event-planner/*'
          element={
            <SuspensedView>
              <Frequencies />
            </SuspensedView>
          }
        />
        <Route
          path='sites/:asset_id/:site_id/event-planner/*'
          element={
            <SuspensedView>
              <Frequencies />
            </SuspensedView>
          }
        />
        <Route
          path='assets/*'
          element={
            <SuspensedView>
              <Assets />
            </SuspensedView>
          }
        />
        <Route
          path='inspection-listing/*'
          element={
            <SuspensedView>
              <InspectionListing />
            </SuspensedView>
          }
        />
        <Route
          path='inspection-templates/*'
          element={
            <SuspensedView>
              <InspectionTemplates />
            </SuspensedView>
          }
        />
        <Route
          path='manage-orders/*'
          element={
            <SuspensedView>
              <Orders />
            </SuspensedView>
          }
        />
        <Route
          path='your-credits/*'
          element={
            <SuspensedView>
              <Orders />
            </SuspensedView>
          }
        />
        <Route
          path='asset-binding/*'
          element={
            <SuspensedView>
              <Tags />
            </SuspensedView>
          }
        />
        <Route
          path='subscriptions/*'
          element={
            <SuspensedView>
              <Subscriptions />
            </SuspensedView>
          }
        />
        <Route
          path='transaction-history/*'
          element={
            <SuspensedView>
              <TransactionHistory />
            </SuspensedView>
          }
        />
        {(IsRootUserOrAdmin() || CheckAccess('configuration.index')) && (
          <Route
            path='configurations'
            element={
              <SuspensedView>
                <Configurations />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='groups/*'
          element={
            <SuspensedView>
              <Groups />
            </SuspensedView>
          }
        />
        {CheckAccess('mappings.create') && (
          <Route
            path='mappings'
            element={
              <SuspensedView>
                <Mappings />
              </SuspensedView>
            }
          />
        )}
        {IsRootUserOrAdmin() && (
          <Route
            path='category/*'
            element={
              <SuspensedView>
                <Categories />
              </SuspensedView>
            }
          />
        )}
        {IsRootUserOrAdmin() && (
          <Route
            path='account-anonymization-requests/*'
            element={
              <SuspensedView>
                <DataAnonymization />
              </SuspensedView>
            }
          />
        )}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<{children: ReactNode}> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
